import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEOHead
 * - Accepts page-level title, description, keywords, canonical path, breadcrumbs and extraSchemas
 * - Injects global LATAM-focused Organization/WebSite/FAQ schemas on every page
 * - Keeps compatibility with pages that still call <SEOHead /> with no props (home uses index.html)
 */
const SITE_URL = 'https://cloudbase.cl';
const DEFAULT_TITLE = 'Cloudbase - ERP y CRM para Latinoamérica | Plataforma Integrada Multi-Industria';
const DEFAULT_DESCRIPTION =
  'Cloudbase es la plataforma ERP y CRM integrada para Latinoamérica y España. Coworks, pymes, inmobiliarias, multifamilias y gimnasios con clientes en Chile, México, Argentina, Brasil, Estados Unidos y España.';
const DEFAULT_KEYWORDS =
  'ERP Latinoamérica, CRM LATAM, software gestión empresarial, SaaS LATAM, plataforma multi-industria, Cloudbase';
const DEFAULT_OG_IMAGE = `${SITE_URL}/android-chrome-512x512.png`;

const AREA_SERVED = [
  { '@type': 'Country', name: 'Chile' },
  { '@type': 'Country', name: 'México' },
  { '@type': 'Country', name: 'Argentina' },
  { '@type': 'Country', name: 'Colombia' },
  { '@type': 'Country', name: 'Perú' },
  { '@type': 'Country', name: 'Brasil' },
  { '@type': 'Country', name: 'Ecuador' },
  { '@type': 'Country', name: 'Uruguay' },
  { '@type': 'Country', name: 'Paraguay' },
  { '@type': 'Country', name: 'Bolivia' },
  { '@type': 'Country', name: 'Venezuela' },
  { '@type': 'Country', name: 'España' },
  { '@type': 'Country', name: 'Estados Unidos' }
];

const buildBreadcrumbSchema = (breadcrumbs) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.map((b, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: b.name,
    item: b.item.startsWith('http') ? b.item : `${SITE_URL}${b.item}`
  }))
});

const SEOHead = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  path = '',
  image = DEFAULT_OG_IMAGE,
  breadcrumbs = [],
  extraSchemas = []
}) => {
  const canonicalUrl = `${SITE_URL}${path}`;

  const globalSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: 'Cloudbase',
        alternateName: ['Cloudbase Latam', 'CloudBase Latinoamérica'],
        url: SITE_URL,
        logo: {
          '@type': 'ImageObject',
          url: `${SITE_URL}/android-chrome-512x512.png`
        },
        description:
          'Plataforma ERP y CRM integrada para gestión empresarial multi-industria en Latinoamérica y España. Con clientes en Chile, México, Argentina, Brasil, Estados Unidos y España.',
        areaServed: AREA_SERVED,
        sameAs: ['https://github.com/CloudBaseLatam']
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: 'Cloudbase',
        inLanguage: ['es', 'es-419'],
        publisher: { '@id': `${SITE_URL}/#organization` }
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '¿Cloudbase sirve fuera de Chile?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí. Cloudbase está diseñado para empresas en toda Latinoamérica y España. Actualmente tenemos clientes en Chile, México, Argentina, Brasil, Estados Unidos y España, y damos soporte a más de 15 países LATAM.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Cómo se adapta Cloudbase a diferentes industrias?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Cloudbase es una plataforma modular que se configura según cada industria. Nuestros módulos (CRM, Ventas, Inventario, Contabilidad, RRHH, Proyectos, Helpdesk, SurveyHub, Control de Asistencia, SpacesHub, Logística, Facturación DTE, Control de Acceso, MeetingRoom, App Personalizada y Cloudbase Endpoint) se adaptan a coworks, pymes, inmobiliarias, multifamily y gimnasios.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Puedo personalizar la plataforma con mi marca?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí. Ofrecemos white-label completo: logo, colores, tipografía, dominio propio y aplicación móvil en App Store y Play Store con tu marca. También personalizamos las pantallas de los dispositivos SKO y SMO para que muestren tu identidad.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Tiene prueba gratuita?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí. Ofrecemos prueba gratuita de 14 días sin tarjeta de crédito. Los planes comienzan desde 39 USD por usuario al mes.'
            }
          }
        ]
      }
    ]
  };

  const breadcrumbSchema = breadcrumbs.length > 0 ? buildBreadcrumbSchema(breadcrumbs) : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Cloudbase" />
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />
      <meta name="language" content="Spanish" />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="es_419" />
      <meta property="og:site_name" content="Cloudbase" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* AI Crawlers metadata (LATAM-wide) */}
      <meta
        name="ai:purpose"
        content="Cloudbase es una plataforma SaaS ERP y CRM integrada para gestión empresarial multi-industria en Latinoamérica y España."
      />
      <meta
        name="ai:industries"
        content="coworking, pymes y empresas, inmobiliarias, multifamilias, gimnasios, espacios compartidos, servicios profesionales, logística"
      />
      <meta
        name="ai:features"
        content="CRM, ERP, ventas, inventario, contabilidad, RRHH, proyectos, helpdesk, facturación electrónica, control de asistencia, control de acceso, reservas de espacios, encuestas, app móvil, monitoreo de endpoints"
      />
      <meta
        name="ai:target_audience"
        content="Empresas en Chile, México, Argentina, Colombia, Perú, Brasil, Estados Unidos, España y toda Latinoamérica. Coworks, inmobiliarias, administradores de edificios multifamily, gimnasios y pymes."
      />
      <meta
        name="ai:location"
        content="Latinoamérica + España + Estados Unidos"
      />
      <meta
        name="ai:countries_served"
        content="CL, MX, AR, CO, PE, BR, EC, UY, PY, BO, VE, CR, PA, GT, DO, HN, NI, SV, CU, ES, US"
      />
      <meta
        name="ai:existing_clients_countries"
        content="Chile, México, Argentina, Brasil, Estados Unidos, España"
      />
      <meta name="ai:language" content="Español" />
      <meta name="chatgpt:crawl" content="enabled" />

      {/* Structured Data - Global */}
      <script type="application/ld+json">{JSON.stringify(globalSchema)}</script>

      {/* Breadcrumbs */}
      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}

      {/* Page-specific product/service schemas */}
      {extraSchemas.map((schema, i) => (
        <script key={`extra-schema-${i}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
